import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import Landing from "@/pages/public/Landing";
import Login from "@/pages/public/Login";
import About from "@/pages/public/About";
import Features from "@/pages/public/Features";
import Contact from "@/pages/public/Contact";
import Guide from "@/pages/public/Guide";
import AuthCallback from "@/pages/public/AuthCallback";
import DashboardShell from "@/pages/DashboardShell";
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/master/Products";
import Materials from "@/pages/master/Materials";
import Suppliers from "@/pages/master/Suppliers";
import LaborTypes from "@/pages/master/LaborTypes";
import Machines from "@/pages/master/Machines";
import Units from "@/pages/master/Units";
import Locations from "@/pages/master/Locations";
import OverheadCats from "@/pages/master/OverheadCats";
import EnvCategories from "@/pages/master/EnvCategories";
import MixDesigns from "@/pages/MixDesigns";
import MixDesignEditor from "@/pages/MixDesignEditor";
import Batches from "@/pages/Batches";
import BatchEditor from "@/pages/BatchEditor";
import HppList from "@/pages/HppList";
import HppDetail from "@/pages/HppDetail";
import EnvCosts from "@/pages/EnvCosts";
import Pricing from "@/pages/Pricing";
import Comparison from "@/pages/Comparison";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Members from "@/pages/Members";
import AuditLog from "@/pages/AuditLog";
import DataReferences from "@/pages/DataReferences";
import Organizations from "@/pages/Organizations";

function ProtectedShell({ children }) {
  const { loading, user } = useAuth();
  const location = useLocation();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="flex items-center gap-3 text-slate-500">
        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse [animation-delay:150ms]" />
        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse [animation-delay:300ms]" />
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Handle Google Auth callback: session_id in hash fragment
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/tentang" element={<About />} />
      <Route path="/fitur" element={<Features />} />
      <Route path="/panduan" element={<Guide />} />
      <Route path="/kontak" element={<Contact />} />
      <Route path="/login" element={<Login />} />
      <Route path="/app" element={<ProtectedShell><DashboardShell /></ProtectedShell>}>
        <Route index element={<Dashboard />} />
        <Route path="master/products" element={<Products />} />
        <Route path="master/materials" element={<Materials />} />
        <Route path="master/suppliers" element={<Suppliers />} />
        <Route path="master/labor" element={<LaborTypes />} />
        <Route path="master/machines" element={<Machines />} />
        <Route path="master/units" element={<Units />} />
        <Route path="master/locations" element={<Locations />} />
        <Route path="master/overhead" element={<OverheadCats />} />
        <Route path="master/env-categories" element={<EnvCategories />} />
        <Route path="mix-designs" element={<MixDesigns />} />
        <Route path="mix-designs/:id" element={<MixDesignEditor />} />
        <Route path="mix-designs/new" element={<MixDesignEditor />} />
        <Route path="batches" element={<Batches />} />
        <Route path="batches/:id" element={<BatchEditor />} />
        <Route path="batches/new" element={<BatchEditor />} />
        <Route path="hpp" element={<HppList />} />
        <Route path="hpp/:id" element={<HppDetail />} />
        <Route path="env-costs" element={<EnvCosts />} />
        <Route path="pricing" element={<Pricing />} />
        <Route path="comparison" element={<Comparison />} />
        <Route path="references" element={<DataReferences />} />
        <Route path="reports" element={<Reports />} />
        <Route path="settings" element={<Settings />} />
        <Route path="members" element={<Members />} />
        <Route path="organizations" element={<Organizations />} />
        <Route path="audit" element={<AuditLog />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
